import { SIDE } from 'constants/enums';
import { decimalMultiplication } from 'helpers/assetUtils';
import { calculateMaxLossAtSettlementByLegs } from 'helpers/formulas';
import { division } from 'helpers/math';
import { divide } from 'helpers/ramda';
import { addZeroesUntilCorrectPrecision, cropAfterDecimals, isNan } from 'helpers/utils';
import { IBuySell } from 'reducers/l2Orderbook';
import { selectedProductSpotPriceSelector } from 'stores/selectedProduct/selectedProduct.selectors';
import IOrderbook from 'types/IOrderbook';
import { ComboType, ContractType, Leg, Product } from 'types/IProducts';
import { IMultiCollateral } from 'types/IVariableStore';

import { notional, offsetPositionSize } from './commonFormulae';
import { getImpactFromPremium } from './formulae.helpers';

const optionsAvailableBalance = ({
  multiCollateralData,
}: {
  multiCollateralData: IMultiCollateral;
}) => multiCollateralData?.available_margin_long_options ?? 0;

const getEntryPriceToCalculateOffsetMax = ({
  availableBalance,
  orderbook,
  product,
  side,
  limitPrice,
}: {
  availableBalance: number;
  orderbook: IOrderbook;
  product: Product;
  side: SIDE;
  limitPrice: number | null;
}) => {
  // impact price from orderbook assuming there’s no fee and all the AB can be used for the order.
  const { impactPrice: impactByBalance } = getImpactFromPremium(
    Number(availableBalance),
    orderbook,
    product
  );

  if (isNan(impactByBalance)) {
    throw new Error('Impact price is NaN');
  }

  // Buy: If ImpactPrice < Limit Price, then Entry Price = Imp else Entry Price = Limit Price
  // Sell: If ImpactPrice > Limit Price, Entry Price = Imp else Entry Price = Limit Price
  let entryPrice = impactByBalance;
  if (limitPrice) {
    if (side === SIDE.BUY && impactByBalance > limitPrice) {
      entryPrice = limitPrice;
    }
    if (side === SIDE.SELL && impactByBalance < limitPrice) {
      entryPrice = limitPrice;
    }
  }

  return entryPrice;
};

const getMaxNumberofContracts = ({
  availableBalance,
  entryPrice,
  contractValue,
  feePerContract,
}: {
  availableBalance: number;
  entryPrice: number;
  contractValue: number;
  feePerContract: number;
}) => {
  return division(availableBalance, entryPrice * contractValue + 3 * feePerContract);
};

const getFeePerContract = ({
  entryPrice,
  spotPrice,
  commissionRate,
  contractValue,
}: {
  entryPrice: number;
  spotPrice: number;
  commissionRate: number;
  contractValue: number;
}) =>
  Math.min(0.1 * entryPrice, Number(spotPrice) * Number(commissionRate)) *
  Number(contractValue);

const getOffsetMax = ({
  maxQuantity,
  totalPositionSize,
  positionSizeLimit,
}: {
  maxQuantity: number;
  totalPositionSize: number;
  positionSizeLimit: number;
}) => {
  return Math.min(positionSizeLimit - Math.max(totalPositionSize, 0.0), maxQuantity);
};

const getBuySideOffsetMax = ({
  availableBalance,
  entryPrice,
  contractValue,
  feePerContract,
  totalPositionSize,
  product,
  side,
}: {
  availableBalance: number;
  entryPrice: number;
  contractValue: number;
  feePerContract: number;
  totalPositionSize: number;
  product: Product;
  side: SIDE;
}) => {
  // MaxNumberofContracts = AB/ (Entry Price * contract_value + 3* Fee)
  const maxNumberofContracts = getMaxNumberofContracts({
    availableBalance,
    entryPrice,
    contractValue,
    feePerContract,
  });
  // buyMaxQuantity = MaxNumberofContracts + offsetted totalPositionSize
  const buyMaxQuantity = offsetPositionSize({
    maxNumberofContracts,
    totalPositionSize,
    side,
  });
  const offSettedMax = getOffsetMax({
    maxQuantity: buyMaxQuantity,
    totalPositionSize,
    positionSizeLimit: Number(product?.position_size_limit),
  });

  return offSettedMax;
};

const getMarginRequired = ({ contractType, productSpecs, spotPrice, leverage }) => {
  let marginRequired: number = 0;
  if (contractType === ContractType.OptionsCombos) {
    const { legs } = productSpecs;
    // Max loss at settlement is the max loss that can be incurred at the time of settlement
    // maxLossAtSettlement = sum of all the strike prices of the legs where the side is sell
    const maxLossAtSettlement = calculateMaxLossAtSettlementByLegs(legs ?? []);
    marginRequired = Math.min(
      division(Number(spotPrice), leverage),
      Math.abs(maxLossAtSettlement)
    );
  } else {
    marginRequired = division(Number(spotPrice), leverage);
  }

  return marginRequired;
};

const sellSideOffsetMax = ({
  availableBalance,
  contractValue,
  feePerContract,
  totalPositionSize,
  product,
  contractType,
  spotPrice,
  leverage,
  side,
}: {
  availableBalance: number;
  contractValue: number;
  feePerContract: number;
  totalPositionSize: number;
  product: Product;
  contractType: ContractType;
  spotPrice: number;
  leverage: number;
  side: SIDE;
}) => {
  const productSpecs = product?.product_specs;

  // maxLossAtSettlement = sum of all the strike prices of the legs where the side is sell
  // marginRequired = min(spotPrice/leverage, maxLossAtSettlement)
  const marginRequired: number = getMarginRequired({
    contractType,
    productSpecs,
    spotPrice,
    leverage,
  });

  // maxNumberofContracts = availableBalance/ (marginRequired * contract_value + 3* Fee)
  const maxNumberofContracts = getMaxNumberofContracts({
    availableBalance,
    entryPrice: marginRequired,
    contractValue,
    feePerContract,
  });
  // sellMax = maxNumberofContracts + offsetted totalPositionSize
  const sellMax = offsetPositionSize({
    maxNumberofContracts,
    totalPositionSize,
    side,
  });
  // offsettedMax = min((product.position_size_limit - totalPositionSize), sellMax)
  const offSettedMax = getOffsetMax({
    maxQuantity: sellMax,
    totalPositionSize,
    positionSizeLimit: Number(product?.position_size_limit),
  });

  return offSettedMax;
};

/**
 * Convert user inputed quantity to contracts for options
 * @param inputedQuantity - The quantity to convert
 * @param selectedProduct - The selected product
 * @param selectedCurrency - The selected currency from quantity dropdown
 * @returns The number of contracts
 */
const quantityToContractsForOptions = ({
  inputedQuantity,
  selectedProduct,
  selectedCurrency,
}: {
  inputedQuantity: number;
  selectedProduct: Product;
  selectedCurrency: string;
}) => {
  if (!selectedProduct) {
    throw new Error('Selected product is required');
  }
  if (!selectedCurrency) {
    throw new Error('Selected currency is required');
  }
  if (!inputedQuantity) {
    throw new Error('Inputed quantity is required');
  }

  const settlingAsset = selectedProduct?.settling_asset;
  const settlingAssetSymbol = settlingAsset?.symbol;
  const underlyingAsset = selectedProduct?.underlying_asset;
  const underlyingAssetSymbol = underlyingAsset?.symbol;
  const tickSize = selectedProduct?.tick_size;

  if (!settlingAsset || !underlyingAsset) {
    throw new Error('Settling asset and underlying asset are required');
  }

  if (!settlingAsset || !underlyingAsset) {
    throw new Error('Settling asset and underlying asset are required');
  }

  if (!settlingAsset || !underlyingAsset) {
    throw new Error('Settling asset and underlying asset are required');
  }

  const contractValue = Number(selectedProduct?.contract_value ?? '1');
  const spotPrice = selectedProductSpotPriceSelector(Number(tickSize));

  if (selectedCurrency === settlingAssetSymbol) {
    const price: number = Number(decimalMultiplication(contractValue, spotPrice));
    return parseInt(String(divide(inputedQuantity, price)), 10);
  }

  if (selectedCurrency === underlyingAssetSymbol) {
    return parseInt(String(divide(inputedQuantity, contractValue)), 10);
  }

  return parseInt(String(inputedQuantity), 10);
};

/**
 * Convert contracts to quantity for options
 * @param orderSize - The number of contracts
 * @param selectedProduct - The selected product
 * @param selectedCurrency - The selected currency from quantity dropdown
 * @returns The quantity
 */
const contractsToQuantityForOptions = ({
  orderSize,
  selectedProduct,
  selectedCurrency,
  forErrMsg = false,
}: {
  orderSize: number;
  selectedProduct: Product;
  selectedCurrency: string;
  forErrMsg?: boolean;
}) => {
  const settlingAsset = selectedProduct?.settling_asset;
  const settlingAssetSymbol = settlingAsset?.symbol;
  const underlyingAsset = selectedProduct?.underlying_asset;
  const underlyingAssetSymbol = underlyingAsset?.symbol;
  const tickSize = selectedProduct?.tick_size;

  const contractValue = Number(selectedProduct?.contract_value ?? '1');
  const spotPrice = selectedProductSpotPriceSelector(Number(tickSize));

  if (isNan(spotPrice)) {
    throw new Error('Spot price is NaN');
  }

  const formula = orderSize * contractValue;
  if (selectedCurrency === settlingAssetSymbol) {
    const price = cropAfterDecimals(
      formula * Number(spotPrice),
      settlingAsset?.minimum_precision
    );
    if (forErrMsg) {
      return price;
    }
    return addZeroesUntilCorrectPrecision(price, settlingAsset?.minimum_precision);
  }

  if (selectedCurrency === underlyingAssetSymbol) {
    const price = cropAfterDecimals(formula, underlyingAsset?.minimum_precision);
    if (forErrMsg) {
      return price;
    }
    return addZeroesUntilCorrectPrecision(price, underlyingAsset?.minimum_precision);
  }

  return 0;
};

const calcMaxCashFlowAtSettlement = (
  contractType: ContractType,
  legs: Leg[],
  optionsComboType: ComboType
) => {
  if (
    contractType === ContractType.MoveOptions ||
    contractType === ContractType.CallOptions ||
    contractType === ContractType.PutOptions ||
    contractType === ContractType.TurboCallOptions ||
    contractType === ContractType.TurboPutOptions
  ) {
    return Number.MAX_VALUE;
  }
  if (contractType === ContractType.OptionsCombos) {
    if (optionsComboType === ComboType.CallSpread) {
      return legs.reduce((acc, leg: Leg) => {
        if (leg.side === 'buy') {
          return acc - (Number(leg?.strike_price) ?? 0);
        }
        return acc + (Number(leg?.strike_price) ?? 0);
      }, 0);
    }
    if (optionsComboType === ComboType.PutSpread) {
      return legs.reduce((acc, leg: Leg) => {
        if (leg.side === 'buy') {
          return acc + (Number(leg?.strike_price) ?? 0);
        }
        return acc - (Number(leg?.strike_price) ?? 0);
      }, 0);
    }
  }
  return 0;
};

// Size is positive for buy order, negative for sell order
const calculateEstimateMarginForPlaceOrderOptions = (
  contractSize: number,
  price: number,
  selectedProduct: Product,
  side: SIDE,
  leverage: number
) => {
  const size = contractSize;
  const tickSize = selectedProduct?.tick_size;
  const spotPrice = selectedProductSpotPriceSelector(Number(tickSize));
  const isBuyOrder = side === SIDE.BUY;

  const contractType = selectedProduct?.contract_type;

  if (!contractType) {
    throw new Error('Contract type is required');
  }
  if (!spotPrice || isNan(spotPrice)) {
    throw new Error('Spot price is required');
  }

  if (isBuyOrder) {
    return notional(size, price, selectedProduct);
  }
  const maxCashflowAtSettlement = calcMaxCashFlowAtSettlement(
    contractType,
    selectedProduct?.product_specs?.legs ?? [],
    selectedProduct?.product_specs?.combo_type ?? ComboType.CallSpread
  );
  const notionalValue = notional(size, Number(spotPrice), selectedProduct)!;
  const maxCashFlowNotional = notional(size, maxCashflowAtSettlement, selectedProduct)!;

  if (isNan(notionalValue)) {
    throw new Error('Notional value is NaN');
  }
  if (isNan(maxCashFlowNotional)) {
    throw new Error('Max cash flow notional is NaN');
  }
  if (!notionalValue) {
    throw new Error('Notional value is zero');
  }
  if (!maxCashFlowNotional) {
    throw new Error('Max cash flow notional is zero');
  }
  const minValue = Math.min(notionalValue / leverage, maxCashFlowNotional);
  return minValue;
};

/**
 * used to calculate the offset max for options, when we click on percentages
 * @param param0 - The available balance, orderbook, product, limit price, side, spot price, taker commission rate, total position size, and leverage
 * @returns The offset max for the options
 * Traceback: src/components/placeorder_v2/QuantityInput/quantityChange.thunk.ts:18
 */
const getOptionsOffsetMax = ({
  availableBalance,
  orderbook,
  product,
  limitPrice,
  side,
  spotPrice,
  commissionRate,
  totalPositionSize,
  leverage,
}: {
  availableBalance: number;
  orderbook: IBuySell[];
  product: Product;
  limitPrice: number | null;
  side: SIDE;
  spotPrice: number;
  commissionRate: number;
  totalPositionSize: number;
  leverage: number;
}) => {
  try {
    const contractValue = Number(product?.contract_value);
    const contractType = product?.contract_type;

    // Impact price: The average price per contract that can be achieved with the given premium.
    // Buy: If ImpactPrice < Limit Price, then Entry Price = ImpactPrice else Entry Price = Limit Price
    // Sell: If ImpactPrice > Limit Price, Entry Price = ImpactPrice else Entry Price = Limit Price
    const entryPrice = getEntryPriceToCalculateOffsetMax({
      availableBalance,
      orderbook,
      product,
      side,
      limitPrice,
    });

    // Fee = min (10% * entry price, spot price * fee_rate) * contract_value
    // fee capping at 10% of est. premium for options
    const feePerContract = getFeePerContract({
      entryPrice,
      spotPrice,
      commissionRate,
      contractValue,
    });

    switch (side) {
      case SIDE.BUY:
        // MaxNumberofContracts = availableBalance/ (Entry Price * contract_value + 3* Fee)
        // buyMaxQuantity = MaxNumberofContracts + offsetted totalPositionSize
        // offsettedMax = min((product.position_size_limit - totalPositionSize), buyMaxQuantity)
        return getBuySideOffsetMax({
          availableBalance,
          entryPrice,
          contractValue,
          feePerContract,
          totalPositionSize,
          product,
          side,
        });
      case SIDE.SELL:
        // maxLossAtSettlement = sum of all the strike prices of the legs where the side is sell
        // marginRequired = min(spotPrice/leverage, maxLossAtSettlement)
        // maxNumberofContracts = availableBalance/ (marginRequired * contract_value + 3* Fee)
        // sellMax = maxNumberofContracts + offsetted totalPositionSize
        // offsettedMax = min((product.position_size_limit - totalPositionSize), sellMax)
        return sellSideOffsetMax({
          availableBalance,
          contractValue,
          feePerContract,
          totalPositionSize,
          product,
          contractType,
          spotPrice,
          leverage,
          side,
        });
      default:
        throw new Error(`Unsupported side: ${side}`);
    }
  } catch (error) {
    console.error('Error in getOptionsOffsetMax:', error);
    throw error;
  }
};

const maxQuantityForTurboOptions = ({
  availableBalance,
  leverage,
  commissionRate,
  spotPrice,
  entryPrice,
  side,
}: {
  availableBalance: number;
  leverage: number;
  commissionRate: number;
  spotPrice: number;
  entryPrice: number;
  side: SIDE;
}) => {
  const totalCommission = 2 * commissionRate;
  const totalCost = spotPrice * totalCommission;
  switch (side) {
    case SIDE.BUY: {
      const totalCostForBuy = totalCost + entryPrice;
      return availableBalance / totalCostForBuy;
    }
    case SIDE.SELL: {
      const totalCostForSell = totalCost + spotPrice / leverage;
      return availableBalance / totalCostForSell;
    }
    default:
      throw new Error(`Unsupported side: ${side}`);
  }
};

export {
  calculateEstimateMarginForPlaceOrderOptions,
  contractsToQuantityForOptions,
  getBuySideOffsetMax,
  getEntryPriceToCalculateOffsetMax,
  getFeePerContract,
  getMarginRequired,
  getMaxNumberofContracts,
  getOptionsOffsetMax,
  maxQuantityForTurboOptions,
  optionsAvailableBalance,
  quantityToContractsForOptions,
  sellSideOffsetMax,
};
